import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { ArrowLeft, Send, ArrowDownToLine, ExternalLink, Search, Filter, Calendar } from 'lucide-react';
import { Input } from "@/components/ui/input";
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NonCustodialHistoryPage({ user, onLogout }) {
  const { address, isConnected } = useAccount();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, sent, received

  useEffect(() => {
    if (user && address) {
      fetchTransactions();
    }
  }, [user, address]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('routeflow_token');
      const response = await axios.get(`${API}/transactions/history/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = 
      tx.to_identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.to_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.tx_hash?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterType === 'all' ||
      (filterType === 'sent' && tx.from_address?.toLowerCase() === address?.toLowerCase()) ||
      (filterType === 'received' && tx.to_address?.toLowerCase() === address?.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  const getExplorerUrl = (txHash) => {
    return `https://polygonscan.com/tx/${txHash}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="text-center">Connect Your Wallet</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-4">
                Please connect your wallet to view transaction history
              </p>
              <Button onClick={() => window.location.href = '/crypto/wallet'}>
                Go to Wallet
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/crypto/wallet'}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Transaction History
            </h1>
            <p className="text-gray-600 mt-1">
              View all your past transfers
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Filters & Search */}
          <Card className="border-2 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by @username, address, or transaction hash..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Filter Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant={filterType === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterType('all')}
                    size="sm"
                  >
                    All
                  </Button>
                  <Button
                    variant={filterType === 'sent' ? 'default' : 'outline'}
                    onClick={() => setFilterType('sent')}
                    size="sm"
                  >
                    Sent
                  </Button>
                  <Button
                    variant={filterType === 'received' ? 'default' : 'outline'}
                    onClick={() => setFilterType('received')}
                    size="sm"
                  >
                    Received
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction List */}
          {loading ? (
            <Card className="border-emerald-200">
              <CardContent className="py-12 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading transactions...</p>
              </CardContent>
            </Card>
          ) : filteredTransactions.length === 0 ? (
            <Card className="border-emerald-200">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No transactions found' : 'No transactions yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm 
                    ? 'Try adjusting your search or filters' 
                    : 'Your transaction history will appear here after you send or receive crypto'}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => window.location.href = '/crypto/send'}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Your First Transfer
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredTransactions.map((tx, index) => (
                <Card key={tx.id || index} className="border-emerald-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.from_address?.toLowerCase() === address?.toLowerCase()
                            ? 'bg-red-100'
                            : 'bg-green-100'
                        }`}>
                          {tx.from_address?.toLowerCase() === address?.toLowerCase() ? (
                            <Send className="w-5 h-5 text-red-600" />
                          ) : (
                            <ArrowDownToLine className="w-5 h-5 text-green-600" />
                          )}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${
                              tx.from_address?.toLowerCase() === address?.toLowerCase()
                                ? 'text-red-600'
                                : 'text-green-600'
                            }`}>
                              {tx.from_address?.toLowerCase() === address?.toLowerCase() ? 'Sent' : 'Received'}
                            </span>
                            {tx.to_identifier && (
                              <span className="text-sm text-gray-600">
                                {tx.from_address?.toLowerCase() === address?.toLowerCase() ? 'to' : 'from'} {tx.to_identifier}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-sm text-gray-500 mb-2">
                            {formatDate(tx.created_at)}
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="font-mono truncate">
                              {tx.to_address?.slice(0, 10)}...{tx.to_address?.slice(-8)}
                            </span>
                            {tx.tx_hash && (
                              <a
                                href={getExplorerUrl(tx.tx_hash)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                              >
                                View on Explorer
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>

                          {tx.note && (
                            <p className="text-sm text-gray-600 mt-2 italic">
                              "{tx.note}"
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          tx.from_address?.toLowerCase() === address?.toLowerCase()
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}>
                          {tx.from_address?.toLowerCase() === address?.toLowerCase() ? '-' : '+'}
                          {parseFloat(tx.amount).toFixed(4)} {tx.currency}
                        </div>
                        {tx.service_fee > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Fee: {parseFloat(tx.service_fee).toFixed(4)} {tx.currency}
                          </div>
                        )}
                        <div className={`text-xs mt-1 px-2 py-0.5 rounded-full inline-block ${
                          tx.status === 'completed' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tx.status || 'Completed'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
